import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { SetupChecklistLayout } from "Components/Dashboard/SetupChecklist/SetupChecklistLayout";
import { SetupChecklistRow } from "Components/Dashboard/SetupChecklist/SetupChecklistRow";
import type {
  SetupStepId,
  SetupStepRowState,
} from "Components/Dashboard/SetupChecklist/types";
import { BlDivider } from "Components/Shared/BlDivider";
import { useIsUserInMooneLite } from "Hooks/useIsUserInMooneLite";
import { useSetupChecklistSteps } from "Hooks/useSetupChecklistSteps";
import { Fragment, type ReactNode } from "react";
import { Resources, useResource } from "Translations/Resources";
import { appUrl } from "Utils/UrlUtils";

const RESOURCES = Resources.Components.SetupChecklist;
const STEPS_RESOURCES = Resources.Components.SetupChecklist.Steps;

const STEP_ORDER: ReadonlyArray<SetupStepId> = [
  "connectBank",
  "verifyIdentity",
  "setPrimaryAccount",
];

type StepConfig = {
  title: string;
  description: ReactNode;
  ctaText: string;
  ctaUrl: string;
  isUpcomingLinkHidden?: boolean;
  blockedDescription?: string;
};

export const SetupChecklistCard = () => {
  const { t } = useResource();
  const { isLiteTier } = useIsUserInMooneLite();
  const { isReady, isComplete, steps, completedCount, totalCount } =
    useSetupChecklistSteps();

  if (!isReady || isComplete || !steps) {
    return null;
  }

  const stepConfigs: Record<SetupStepId, StepConfig> = {
    connectBank: {
      title: t(STEPS_RESOURCES.ConnectBank.Title),
      description: t(STEPS_RESOURCES.ConnectBank.Description),
      ctaText: t(STEPS_RESOURCES.ConnectBank.Action),
      ctaUrl: appUrl("banking-auth"),
    },
    setPrimaryAccount: {
      title: t(STEPS_RESOURCES.SetPrimaryAccount.Title),
      description: t(STEPS_RESOURCES.SetPrimaryAccount.Description),
      ctaText: t(STEPS_RESOURCES.SetPrimaryAccount.Action),
      ctaUrl: appUrl("bank-address-list"),
      isUpcomingLinkHidden: true,
      blockedDescription: t(
        STEPS_RESOURCES.SetPrimaryAccount.BlockedDescription,
      ),
    },
    verifyIdentity: {
      title: t(STEPS_RESOURCES.VerifyIdentity.Title),
      description: (
        <>
          {t(STEPS_RESOURCES.VerifyIdentity.Description)}
          <AutoAwesomeIcon
            fontSize="inherit"
            sx={{ ml: 0.5, verticalAlign: "-0.125em" }}
          />
        </>
      ),
      ctaText: t(STEPS_RESOURCES.VerifyIdentity.Action),
      ctaUrl: appUrl("complete-customer-onboarding"),
    },
  };

  const activeStepId = STEP_ORDER.find(
    (stepId) => steps[stepId] === "incomplete",
  );

  const getRowState = (stepId: SetupStepId): SetupStepRowState => {
    if (steps[stepId] === "completed") {
      return "done";
    }

    if (steps[stepId] === "inReview") {
      return "inReview";
    }

    // Accounts only activate once their owner's identity is verified, so a
    // primary account cannot be chosen while verification is under review.
    if (stepId === "setPrimaryAccount" && steps.verifyIdentity === "inReview") {
      return "blocked";
    }

    return stepId === activeStepId ? "active" : "upcoming";
  };

  return (
    <SetupChecklistLayout
      title={t(RESOURCES.Title)}
      subtitle={t(isLiteTier ? RESOURCES.LiteSubtitle : RESOURCES.Subtitle)}
      completedCount={completedCount}
      totalCount={totalCount}
    >
      {STEP_ORDER.map((stepId, index) => {
        const state = getRowState(stepId);
        const { blockedDescription, description, ...config } =
          stepConfigs[stepId];

        return (
          <Fragment key={stepId}>
            {index !== 0 && <BlDivider marginY={0} />}
            <SetupChecklistRow
              state={state}
              stepNumber={index + 1}
              description={
                state === "blocked" ? blockedDescription : description
              }
              {...config}
            />
          </Fragment>
        );
      })}
    </SetupChecklistLayout>
  );
};
